import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useModuleScope } from "../contexts/ModuleScopeContext";
import { useFinanceScope } from "../contexts/FinanceScopeContext";
import { visibleNavFor } from "../lib/rbac";
import HeaderTools from "./HeaderTools";
import Breadcrumbs from "./Breadcrumbs";
import UserMenu from "./UserMenu";
import AuthedShellChrome from "./AuthedShellChrome";
import SecurityBanner from "./SecurityBanner";

export default function AppShell({ title, children }) {
    const { user } = useAuth();
    const navigate = useNavigate();
    const moduleScope = useModuleScope();
    const financeScope = useFinanceScope();

    // When nested inside a ModuleAppShell or MasterFinanceShell, render content only —
    // the outer shell already provides the sidebar + header.
    if (moduleScope || financeScope) {
        return (
            <>
                {title && (
                    <h2 className="text-2xl font-bold asl-navy-text mb-5" data-testid="module-page-title">{title}</h2>
                )}
                {children}
            </>
        );
    }

    // Outside a module/finance shell (admin-only pages like /admin/settings, /users, /audit, /account),
    // module-scoped nav items are hidden — they have no module context to resolve to.
    const nav = visibleNavFor(user, { excludeModuleScoped: true });

    return (
        <div className="min-h-screen flex" data-testid="app-shell">
            {/* Sidebar */}
            <aside className="asl-sidebar" data-testid="app-sidebar">
                <button
                    onClick={() => navigate("/")}
                    className="flex items-center gap-3 px-5 py-5 w-full hover:bg-white/5 text-left"
                    style={{ borderBottom: "1px solid rgba(255,255,255,0.08)" }}
                    data-testid="sidebar-home"
                >
                    <img
                        src="/asl-logo.png"
                        alt="ASL"
                        style={{ height: 36, width: "auto" }}
                        className="asl-logo--washed"
                        data-testid="sidebar-logo"
                    />
                    <div className="leading-tight">
                        <div className="text-white font-bold tracking-wide text-base">
                            ASL
                        </div>
                        <div className="text-[11px] text-[#a8b3cc]">HR Platform · Admin</div>
                    </div>
                </button>

                <nav className="flex-1 overflow-y-auto py-3" data-testid="sidebar-nav">
                    <div className="asl-nav-section">Admin</div>
                    {nav.map((item) => (
                        <NavLink
                            key={item.key}
                            to={item.path}
                            className={({ isActive }) =>
                                `asl-nav-item ${isActive ? "active" : ""}`
                            }
                            data-testid={`nav-${item.key}`}
                        >
                            <span>{item.label}</span>
                        </NavLink>
                    ))}
                    <div className="asl-nav-section mt-4">Workspace</div>
                    <div className="asl-nav-item disabled" title="Pick a module from Home"
                        data-testid="nav-pick-module-hint">
                        <span>Modules live on Home →</span>
                    </div>
                </nav>

                <div
                    className="px-5 py-4 text-[11px] leading-snug"
                    style={{
                        borderTop: "1px solid rgba(255,255,255,0.08)",
                        color: "#8a96b3",
                    }}
                >
                    Managed by Kaafi Hire Employment Services Ltd
                </div>
            </aside>

            {/* Main */}
            <div className="flex-1 flex flex-col min-w-0">
                <header
                    className="bg-white px-8 py-4 flex items-center justify-between"
                    style={{ borderBottom: "1px solid var(--asl-border)" }}
                    data-testid="app-header"
                >
                    <div>
                        <h1
                            className="text-xl font-bold asl-navy-text"
                            data-testid="page-title"
                        >
                            {title}
                        </h1>
                    </div>
                    <div className="flex items-center gap-3">
                        <HeaderTools />
                        <UserMenu />
                    </div>
                </header>

                <Breadcrumbs />
                <SecurityBanner />

                <main className="flex-1 p-8 overflow-auto" data-testid="app-main">
                    {children}
                </main>
            </div>
            <AuthedShellChrome />
        </div>
    );
}
